import { forwardRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useInvoiceTheme } from '../hooks/use-invoice-theme';
import type { InvoiceCar, Payment, Sale } from '../types/car';

/** Map a Sale (with warehouseCar, buyer, payments) to the invoice car shape */
export function saleToInvoiceCar(sale: Sale): InvoiceCar {
  const w = sale.warehouseCar;
  return {
    id: sale.id,
    brandId: w.brandId,
    modelId: w.modelId,
    color: w.color,
    year: w.year,
    note: w.notes || undefined,
    saleNote: sale.notes || undefined,
    salePrice: sale.salePrice,
    deposit: sale.deposit,
    currency: sale.currency,
    soldAt: sale.soldAt,
    createdAt: sale.createdAt,
    updatedAt: sale.updatedAt,
    sellerName: sale.sellerName || undefined,
    sellerIdNo: sale.sellerIdNo || undefined,
    sellerAddress: sale.sellerAddress || undefined,
    sellerContact: sale.sellerContact || undefined,
    buyerName: sale.buyer?.name ?? '',
    buyerIdNo: sale.buyer?.idNo ?? undefined,
    buyerAddress: sale.buyer?.address ?? undefined,
    buyerContact: sale.buyer?.contact ?? '',
    chassisNo: w.chassisNo || undefined,
    plateNumber: w.plateNumber || undefined,
    registeredOwner: w.registeredOwner || undefined,
    brand: w.brand,
    model: w.model,
    payments: sale.payments || [],
  };
}

interface CarInvoiceProps {
  car: InvoiceCar;
  payments?: Payment[];
  isReprint?: boolean;
}

const CarInvoice = forwardRef<HTMLDivElement, CarInvoiceProps>(({ car, payments = [], isReprint = false }, ref) => {
  const { t, language } = useLanguage();
  const { colors } = useInvoiceTheme();
  
  // Custom translation function for invoice - use Kurdish when language is English
  const tInvoice = (key: string) => {
    if (language === 'english') {
      // Import the translations directly to get Kurdish translations
      const kurdishTranslations = {
        'invoice.storeName': 'پێشانگای دارا',
        'invoice.date': 'بەروار',
        'invoice.time': 'کات',
        'invoice.buyerDetails': 'وردەکارییەکانی کڕیار',
        'invoice.sellerDetails': 'وردەکارییەکانی فرۆشیار',
        'invoice.vehicleDetails': 'وردەکارییەکانی ئۆتۆمبێل',
        'invoice.brandModel': 'مارکە/مۆدێل',
        'invoice.yearOfManufacture': 'ساڵی دروستکردن',
        'invoice.chassisNo': 'ژمارەی شاصی',
        'invoice.plateNumber': 'ژمارەی تابلۆ',
        'invoice.registeredOwner': 'خاوەنی ساڵیانە',
        'invoice.color': 'ڕەنگ',
        'invoice.note': 'تێبینی:',
        'invoice.agreementStatement': 'تێبینی: ئەم گرێبەستە واژووکرا دوای ئەوەی تەواوی خاڵەکانی لەلایەن هەردوو لایەن خوێندرایەوە هەروەها ئۆتۆمبێلی بابەت پێش ئەم گرێبەستە لەلایەن کڕیار پشکنینی بۆ کرا.',
        'invoice.buyerSignature': 'واژووی کڕیار',
        'invoice.sellerSignature': 'واژووی فرۆشیار',
        'invoice.transactionDetails': 'وردەکارییەکانی مامەڵە',
        'invoice.financialDetails': 'وردەکارییەکانی پارەدان',
        'invoice.carPriceAgreed': 'نرخی گشتی ئۆتۆمبێل',
        'invoice.payment': 'پارەدان',
        'invoice.totalPaid': 'پارەی دراو',
        'invoice.balanceDue': 'بڕی ماوە',
        'invoice.deposit': 'پارەی پێشەکی',
        'invoice.carId': 'ژمارەی وەسڵ',
        'invoice.name': 'ناو',
        'invoice.nricNo': 'ژمارەی ناسنامە',
        'invoice.address': 'ناونیشان',
        'invoice.contactNo': 'تەلەفۆن',
      };
      return kurdishTranslations[key as keyof typeof kurdishTranslations] || key;
    }
    return t(key);
  };
  
  // Get the appropriate font class based on language
  const getFontClass = () => {
    switch (language) {
      case 'kurdish':
      case 'english': // English should display as Kurdish
        return 'invoice-font-kurdish';
      case 'arabic':
        return 'invoice-font-arabic';
      default:
        return 'invoice-font-kurdish';
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const formatPrice = (price: number, curr: 'IQD' | 'USD' = (car as any).currency ?? 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Calculate total paid amount
  // For initial print: include deposit from car object if no payments yet
  // For reprint: only use payments array (deposit is already included as a payment)
  const totalPaidAmount = isReprint 
    ? payments.reduce((total, payment) => total + payment.amount, 0)
    : (car.deposit || 0) + payments.reduce((total, payment) => total + payment.amount, 0);

  return (
    <div className="hidden">
      <div 
        ref={ref}
        className={`bg-white text-black p-0 m-0 ${getFontClass()}`}
        style={{
          width: '210mm',
          minHeight: '297mm',
          maxWidth: '210mm',
          margin: '0 auto',
          fontSize: '12px',
          lineHeight: '1.4',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header Section */}
        <div className="w-full">
          <div className="flex justify-between items-center relative px-4 py-3">
            <div className="w-48 h-32 flex items-center justify-center">
              {/* Empty space for balance */}
            </div>
            <div className="flex-1 flex justify-center items-center relative">
              <h1 className="text-6xl font-bold relative z-10 text-white whitespace-nowrap">
                {tInvoice('invoice.storeName')}
              </h1>
            </div>
            <div className="w-48 h-32 flex items-center justify-center relative">
              <img 
                src="/DaraCars.png" 
                alt="DaraCars Store" 
                className="w-full h-full object-contain relative z-10"
              />
            </div>
            {/* Single background covering both store name and logo area */}
            <div className="absolute inset-0 opacity-80" style={{ backgroundColor: colors.headerBg }}></div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-4 py-3 flex-grow">
          {/* Car ID, Date and Time */}
          <div className="mb-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-base font-medium">{tInvoice('invoice.date')}: <span className="invoice-number">{formatDate(car.soldAt)}</span></p>
                <p className="text-base font-medium">{tInvoice('invoice.time')}: <span className="invoice-number">{formatTime(car.soldAt)}</span></p>
                <div className="flex items-center">
                  <span className="text-base font-medium mr-2">{tInvoice('invoice.carId')}:</span>
                  <span className="text-base font-bold px-2 py-1 rounded invoice-number" style={{ color: colors.accentColor, backgroundColor: colors.accentBg }}>
                    NO: {car.id}
                  </span>
                </div>
              </div>
              <div className="text-base font-medium text-right space-y-1">
                <div className="invoice-number phone-number">0770 445 0169</div>
                <div className="invoice-number phone-number">0750 205 0003</div>
                <div className="phone-number" dir="rtl">هەولێر، ٤٠م ئازادی</div>
              </div>
            </div>
          </div>

          {/* Buyer and Seller Details Table */}
          <div className="mb-2">
            <div className="border-2 border-gray-400">
              <div className="grid grid-cols-2">
                {/* Seller Details Column */}
                <div className="border-r-2 border-gray-600">
                  <div className="p-2 border-b-2 border-gray-400" style={{ backgroundColor: colors.headerBg }}>
                    <h3 className="text-xl font-semibold text-center text-white">{tInvoice('invoice.sellerDetails')}</h3>
                  </div>
                  <div className="space-y-0">
                    <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                      <div className="py-2 px-2 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                        <span className="font-medium">{tInvoice('invoice.name')}:</span>
                      </div>
                      <div className="py-2 px-2 bg-gray-50 text-right border border-gray-300">
                        <span>{car.sellerName || ''}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                      <div className="py-2 px-2 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                        <span className="font-medium">{tInvoice('invoice.nricNo')}:</span>
                      </div>
                      <div className="py-2 px-2 bg-white text-right border border-gray-300">
                        <span>{car.sellerIdNo || ''}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                      <div className="py-2 px-2 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                        <span className="font-medium">{tInvoice('invoice.address')}:</span>
                      </div>
                      <div className="py-2 px-2 bg-gray-50 text-right border border-gray-300">
                        <span>{car.sellerAddress || ''}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 invoice-table-content">
                      <div className="py-2 px-2 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                        <span className="font-medium">{tInvoice('invoice.contactNo')}:</span>
                      </div>
                      <div className="py-2 px-2 bg-white text-right border border-gray-300">
                        <span>{car.sellerContact || ''}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Buyer Details Column */}
                <div>
                  <div className="p-2 border-b-2 border-gray-400" style={{ backgroundColor: colors.headerBg }}>
                    <h3 className="text-xl font-semibold text-center text-white">{tInvoice('invoice.buyerDetails')}</h3>
                  </div>
                  <div className="space-y-0">
                    <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                      <div className="py-2 px-2 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                        <span className="font-medium">{tInvoice('invoice.name')}:</span>
                      </div>
                      <div className="py-2 px-2 bg-gray-50 text-right border border-gray-300">
                        <span>{car.buyerName || ''}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                      <div className="py-2 px-2 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                        <span className="font-medium">{tInvoice('invoice.nricNo')}:</span>
                      </div>
                      <div className="py-2 px-2 bg-white text-right border border-gray-300">
                        <span>{car.buyerIdNo || ''}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 invoice-table-content border-b-2 border-gray-300">
                      <div className="py-2 px-2 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                        <span className="font-medium">{tInvoice('invoice.address')}:</span>
                      </div>
                      <div className="py-2 px-2 bg-gray-50 text-right border border-gray-300">
                        <span>{car.buyerAddress || ''}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 invoice-table-content">
                      <div className="py-2 px-2 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                        <span className="font-medium">{tInvoice('invoice.contactNo')}:</span>
                      </div>
                      <div className="py-2 px-2 bg-white text-right border border-gray-300">
                        <span>{car.buyerContact || ''}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Vehicle Details Table */}
          <div className="mb-2">
            <div className="border-2 border-gray-400">
              <div className="p-2 border-b-2 border-gray-400" style={{ backgroundColor: colors.headerBg }}>
                <h3 className="text-xl font-semibold text-center text-white">{tInvoice('invoice.vehicleDetails')}</h3>
              </div>
              <div className="space-y-0">
                <div className="grid grid-cols-4 invoice-table-content border-b-2 border-gray-300">
                <div className="py-2 px-2 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                    <span className="font-medium">{tInvoice('invoice.brandModel')}:</span>
                  </div>
                  <div className="py-2 px-2 bg-white text-right border border-gray-300">
                    <span dir="ltr">{car.brand.name} {car.model.name}</span>
                  </div>
                  
                  <div className="py-2 px-2 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                    <span className="font-medium">{tInvoice('invoice.color')}:</span>
                  </div>
                  <div className="py-2 px-2 bg-gray-50 text-right border border-gray-300">
                    <span>{car.color}</span>
                  </div>
                </div>
                <div className="grid grid-cols-4 invoice-table-content border-b-2 border-gray-300">
                <div className="py-2 px-2 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                    <span className="font-medium">{tInvoice('invoice.yearOfManufacture')}:</span>
                  </div>
                  <div className="py-2 px-2 bg-gray-50 text-right border border-gray-300">
                    <span>{car.year}</span>
                  </div>
                  <div className="py-2 px-2 bg-white border-r-2 border-gray-300 text-right border border-gray-300">
                    <span className="font-medium">{tInvoice('invoice.registeredOwner')}:</span>
                  </div>
                  <div className="py-2 px-2 bg-white text-right border border-gray-300">
                    <span>{car.registeredOwner || ''}</span>
                  </div>
                </div>
                <div className="grid grid-cols-4 invoice-table-content border-b-2 border-gray-300">
                  <div className="py-2 px-2 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                    <span className="font-medium">{tInvoice('invoice.chassisNo')}:</span>
                  </div>
                  <div className="py-2 px-2 bg-gray-50 text-right border border-gray-300">
                    <span dir="ltr">{car.chassisNo || ''}</span>
                  </div>
                  <div className="py-2 px-2 bg-gray-50 border-r-2 border-gray-300 text-right border border-gray-300">
                    <span className="font-medium">{tInvoice('invoice.plateNumber')}:</span>
                  </div>
                  <div className="py-2 px-2 bg-gray-50 text-right border border-gray-300">
                    <span dir="ltr">{car.plateNumber || ''}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Agreement Statement */}
          <div className="mb-2">
            <p className="text-base" style={{ color: colors.accentColor }}>
                {tInvoice('invoice.agreementStatement')}
            </p>
          </div>

          {/* Note */}
          {car.note && (
            <div className="mb-2">
              <p className="text-base" style={{ color: colors.noteColor }}>
                <span className="font-medium">{tInvoice('invoice.note')}</span> {car.note}
              </p>
            </div>
          )}

          {/* Sale note (optional, below constant note) */}
          {car.saleNote && (
            <div className="mb-2">
              <p className="text-base" style={{ color: '#dc2626' }}>
                <span className="font-medium">{tInvoice('invoice.note')}</span> {car.saleNote}
              </p>
            </div>
          )}

          {/* Transaction Details and Stamp Area */}
          <div className="grid grid-cols-2 gap-4 mb-[-56px]">
            {/* Financial Details */}
            <div>
              <h3 className="text-xl font-semibold text-center mb-4" style={{ color: colors.titleColor }}>{tInvoice('invoice.financialDetails')}</h3>
              <div className="space-y-2 text-lg">
                <div className="flex justify-between">
                  <span className="font-medium">{tInvoice('invoice.carPriceAgreed')}:</span>
                  <span className="invoice-number text-green-600 font-bold">{formatPrice(car.salePrice)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">{tInvoice('invoice.totalPaid')}:</span>
                  <span className="invoice-number text-green-600 font-bold">{formatPrice(totalPaidAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">{tInvoice('invoice.balanceDue')}:</span>
                  <span className="invoice-number text-red-600 font-bold">{formatPrice(car.salePrice - totalPaidAmount)}</span>
                </div>
              </div>
            </div>

            {/* Stamp Area */}
            <div className="flex flex-col justify-center">
              <div 
                style={{ 
                  width: '100%', 
                  height: '60mm'
                }}
              >
              </div>
            </div>
          </div>

          {/* Signatures */}
          <div className="mb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-base font-medium text-center">{tInvoice('invoice.buyerSignature')}</p>
              </div>
              <div>
                <p className="text-base font-medium text-center">{tInvoice('invoice.sellerSignature')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

CarInvoice.displayName = 'CarInvoice';

export default CarInvoice;
